import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { HelpCircle, Plus, ThumbsUp, Eye, MessageCircle, CheckCircle, Search } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const CommunityQA = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAskModal, setShowAskModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [answerText, setAnswerText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("all");

  const [questionForm, setQuestionForm] = useState({
    title: "",
    content: "",
    subject: "General",
    tags: ""
  });

  const subjects = ["All", "General", "Mathematics", "Science", "English", "Programming", "Career", "Exams"];

  useEffect(() => {
    fetchQuestions();
  }, [selectedSubject]);

  const fetchQuestions = async () => {
    try {
      const params = selectedSubject !== "all" ? `?subject=${selectedSubject}` : "";
      const response = await axios.get(`${API}/community/questions${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data);
    } catch (error) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleAskQuestion = async () => {
    try {
      const form = new FormData();
      form.append("title", questionForm.title);
      form.append("content", questionForm.content);
      form.append("subject", questionForm.subject);
      form.append("tags", questionForm.tags);

      await axios.post(`${API}/community/questions`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Question posted!");
      setShowAskModal(false);
      setQuestionForm({ title: "", content: "", subject: "General", tags: "" });
      fetchQuestions();
    } catch (error) {
      toast.error("Failed to post question");
    }
  };

  const handleViewQuestion = async (question) => {
    setSelectedQuestion(question);
    setShowQuestionModal(true);
    
    try {
      const response = await axios.get(`${API}/community/questions/${question.id}/answers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnswers(response.data);
    } catch (error) {
      toast.error("Failed to load answers");
    }
  };

  const handleVoteQuestion = async (questionId) => {
    try {
      const form = new FormData();
      form.append("vote_type", "upvote");
      
      await axios.post(`${API}/community/questions/${questionId}/vote`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Question upvoted!");
      fetchQuestions();
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const handleAnswerQuestion = async () => {
    if (!answerText.trim()) return;

    try {
      const form = new FormData();
      form.append("content", answerText);
      
      await axios.post(`${API}/community/questions/${selectedQuestion.id}/answers`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Answer posted!");
      setAnswerText("");
      
      const response = await axios.get(`${API}/community/questions/${selectedQuestion.id}/answers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnswers(response.data);
    } catch (error) {
      toast.error("Failed to post answer");
    }
  };

  const handleAcceptAnswer = async (answerId) => {
    try {
      const form = new FormData();
      form.append("question_id", selectedQuestion.id);
      
      await axios.post(`${API}/community/answers/${answerId}/accept`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Answer accepted!");
      
      const response = await axios.get(`${API}/community/questions/${selectedQuestion.id}/answers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnswers(response.data);
    } catch (error) {
      toast.error("Failed to accept answer");
    }
  };

  const filteredQuestions = questions.filter(q =>
    (q.title && q.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (q.content && q.content.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading questions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md">
                <HelpCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Q&A Section</h1>
                <p className="text-gray-600 text-sm md:text-base">Ask questions and share knowledge</p>
              </div>
            </div>
            <Button onClick={() => setShowAskModal(true)} className="shadow-md">
              <Plus className="w-4 h-4 mr-2" />
              Ask Question
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <Card className="mb-6 border-none shadow-md">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  placeholder="Search questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {subjects.map(subj => (
                  <Button
                    key={subj}
                    variant={selectedSubject === subj.toLowerCase() ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedSubject(subj.toLowerCase())}
                  >
                    {subj}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Questions List */}
        <div className="space-y-4">
          {filteredQuestions.length > 0 ? (
            filteredQuestions.map(question => (
              <Card
                key={question.id}
                className="border-none shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleViewQuestion(question)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVoteQuestion(question.id);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <ThumbsUp className="w-4 h-4" />
                      </Button>
                      <span className="text-sm font-semibold">{question.upvotes}</span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{question.subject}</Badge>
                        {question.has_accepted_answer && (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Answered
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="text-lg font-semibold mb-2 hover:text-primary transition-colors">
                        {question.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {question.content}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {question.views} views
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {question.answer_count} answers
                        </span>
                        <span>by {question.username}</span>
                        <span>{formatDistanceToNow(new Date(question.created_at), { addSuffix: true })}</span>
                      </div>
                      
                      {question.tags && question.tags.length > 0 && (
                        <div className="flex gap-2 mt-3">
                          {question.tags.map((tag, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              #{tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-none shadow-md">
              <CardContent className="p-12 text-center">
                <HelpCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No questions found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? "Try adjusting your search" : "Be the first to ask a question!"}
                </p>
                <Button onClick={() => setShowAskModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ask Question
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Ask Question Modal */}
        <Dialog open={showAskModal} onOpenChange={setShowAskModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Ask a Question</DialogTitle>
              <DialogDescription>Get help from the community</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={questionForm.title}
                  onChange={(e) => setQuestionForm({ ...questionForm, title: e.target.value })}
                  placeholder="What's your question?"
                />
              </div>
              <div>
                <Label>Subject</Label>
                <Select value={questionForm.subject} onValueChange={(value) => setQuestionForm({ ...questionForm, subject: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.filter(s => s !== "All").map(subj => (
                      <SelectItem key={subj} value={subj}>{subj}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Details</Label>
                <Textarea
                  value={questionForm.content}
                  onChange={(e) => setQuestionForm({ ...questionForm, content: e.target.value })}
                  placeholder="Provide more details about your question..."
                  rows={6}
                />
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={questionForm.tags}
                  onChange={(e) => setQuestionForm({ ...questionForm, tags: e.target.value })}
                  placeholder="e.g., homework, concept, doubt"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAskModal(false)}>Cancel</Button>
              <Button onClick={handleAskQuestion}>Post Question</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Question Modal */}
        <Dialog open={showQuestionModal} onOpenChange={setShowQuestionModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {selectedQuestion && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{selectedQuestion.subject}</Badge>
                    {selectedQuestion.has_accepted_answer && (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Answered
                      </Badge>
                    )}
                  </div>
                  <DialogTitle className="text-2xl">{selectedQuestion.title}</DialogTitle>
                  <DialogDescription>
                    Asked by {selectedQuestion.username} · {formatDistanceToNow(new Date(selectedQuestion.created_at), { addSuffix: true })}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="py-4">
                  <p className="text-sm whitespace-pre-wrap mb-6">{selectedQuestion.content}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                    <span className="flex items-center gap-1">
                      <ThumbsUp className="w-4 h-4" />
                      {selectedQuestion.upvotes} upvotes
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {selectedQuestion.views} views
                    </span>
                  </div>

                  {/* Answers Section */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-4">{answers.length} Answers</h4>
                    
                    {/* Add Answer */}
                    <div className="mb-6">
                      <Textarea
                        value={answerText}
                        onChange={(e) => setAnswerText(e.target.value)}
                        placeholder="Write your answer..."
                        rows={4}
                        className="mb-2"
                      />
                      <Button onClick={handleAnswerQuestion} size="sm">
                        Post Answer
                      </Button>
                    </div>

                    {/* Answers List */}
                    <div className="space-y-4">
                      {answers.map(answer => (
                        <div
                          key={answer.id}
                          className={`rounded-lg p-4 ${
                            answer.is_accepted ? "bg-green-50 border-2 border-green-200" : "bg-muted/30"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold text-sm">{answer.username}</span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(answer.created_at), { addSuffix: true })}
                                </span>
                                {answer.is_accepted && (
                                  <Badge className="bg-green-100 text-green-700 border-green-200">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Accepted Answer
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm whitespace-pre-wrap">{answer.content}</p>
                              <div className="flex items-center gap-4 mt-3">
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <ThumbsUp className="w-3 h-3" />
                                  {answer.upvotes}
                                </span>
                                {!answer.is_accepted && selectedQuestion.user_id === user.id && (
                                  <Button
                                    onClick={() => handleAcceptAnswer(answer.id)}
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs"
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Accept Answer
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default CommunityQA;